import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../components/Icon";
import { Button } from "../components/ui";
import DateRangePicker from "../components/DateRangePicker";
import GeneratingOverlay from "../components/GeneratingOverlay";
import { useAuth } from "../auth";
import { apiGet } from "../api";
import { workTypeLabel, cateringSessionLabel } from "../constants";
import { formatDateTime, formatYmd } from "../dateutil";
import { downloadCsv, printReport, exportDetailedLogs, exportDeliveries, initialsOf } from "../export";
import { C, R, S } from "../theme";

const TYPES = [
  { key: "checks", label: "Daily Checks", path: "/logs?log_type=inspection", icon: "checkmark-done", color: C.green },
  { key: "wind", label: "Wind Checks", path: "/logs?log_type=wind_check", icon: "speedometer", color: C.blue },
  { key: "maintenance", label: "Repairs", path: "/maintenance", icon: "construct", color: C.amber },
  { key: "training", label: "Staff Training", path: "/training", icon: "school", color: "#8b5cf6" },
  { key: "incidents", label: "Accidents", path: "/incidents", icon: "warning", color: C.red },
  { key: "delivery", label: "Delivery Handovers", path: "/delivery-handovers", icon: "clipboard-outline", color: "#14b8a6" },
  { key: "catering", label: "Food Safety (EHO)", path: "/logs?log_type=catering", icon: "restaurant", color: "#f97316" },
  { key: "cleaning", label: "Cleaning Logs", path: "/logs?log_type=cleaning", icon: "sparkles", color: "#06b6d4" },
];

const sb = (name, ini) => `${name || "-"} (${initialsOf(name, ini)})`;

export default function Reports() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [type, setType] = useState("checks");
  const [attractions, setAttractions] = useState([]);
  const [aid, setAid] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { apiGet("/attractions").then(setAttractions).catch(() => {}); }, []);

  const rangeLabel = () => {
    if (from && to) return from === to ? formatYmd(from) : `${formatYmd(from)} → ${formatYmd(to)}`;
    if (from) return `From ${formatYmd(from)}`;
    if (to) return `Until ${formatYmd(to)}`;
    return "All time";
  };

  const run = async (kind) => {
    setMsg("");
    const t = TYPES.find((x) => x.key === type);
    let path = t.path;
    const q = [];
    if (aid !== "all") q.push(`attraction_id=${aid}`);
    if (from) q.push(`date_from=${from}`);
    if (to) q.push(`date_to=${to}`);
    if (kind === "pdf" && (type === "incidents" || type === "maintenance")) q.push("include_photos=true");
    if (type === "checks" || type === "wind" || type === "catering") q.push("enrich=true"); // guidance notes on multi-day packs
    if (kind === "pdf" && type === "delivery") q.push("full=true"); // photos + signatures for certificates
    if (q.length) path += (path.includes("?") ? "&" : "?") + q.join("&");
    let data;
    try { data = await apiGet(path); } catch (e) { setMsg(e?.message || "Export failed"); return; }
    if (type === "checks") data = data.filter((l) => l.log_type !== "wind_check");
    if (!data.length) { setMsg("No records in the selected range"); return; }
    const company = user?.company_name || "Report";
    const cols = {
      checks: [{ label: "Date", get: (l) => (l.check_date ? formatYmd(l.check_date) : formatDateTime(l.submitted_at)) }, { label: "Attraction", get: (l) => l.attraction_name }, { label: "Result", get: (l) => (l.overall_result || "-").toUpperCase() }, { label: "Signed By", get: (l) => sb(l.signed_by_name, l.signed_by_initials) }],
      wind: [{ label: "Date", get: (l) => formatDateTime(l.submitted_at) }, { label: "Attraction", get: (l) => l.attraction_name }, { label: "Wind (mph)", get: (l) => l.wind_speed }, { label: "High Wind", get: (l) => (l.high_wind ? "YES" : "-") }, { label: "Signed By", get: (l) => sb(l.signed_by_name, l.signed_by_initials) }],
      maintenance: [{ label: "Date", get: (m) => formatYmd(m.work_date) }, { label: "Attraction", get: (m) => m.attraction_name }, { label: "Type", get: (m) => workTypeLabel(m.work_type) }, { label: "Technician", get: (m) => m.technician_name }, { label: "Signed By", get: (m) => sb(m.signed_by_name || m.technician_name, m.signed_by_initials) }, { label: "Description", get: (m) => m.description }],
      training: [{ label: "Date", get: (t2) => formatYmd(t2.date_trained) }, { label: "Staff", get: (t2) => t2.staff_name }, { label: "Attraction", get: (t2) => t2.attraction_name }, { label: "Trainer", get: (t2) => sb(t2.signed_by_name || t2.trainer_name, t2.signed_by_initials) }],
      incidents: [{ label: "Date", get: (i) => formatDateTime(i.occurred_at) }, { label: "Attraction", get: (i) => i.attraction_name }, { label: "Injured", get: (i) => i.injured_name }, { label: "Signed By", get: (i) => sb(i.signed_by_name, i.signed_by_initials) }, { label: "Description", get: (i) => i.description }],
      delivery: [{ label: "Date", get: (d) => formatDateTime(d.submitted_at) }, { label: "Attraction", get: (d) => d.attraction_name }, { label: "Customer", get: (d) => d.customer_name }, { label: "Email", get: (d) => d.customer_email }, { label: "Surface", get: (d) => d.surface_type }, { label: "Delivered By", get: (d) => d.delivered_by || d.driver_name }, { label: "Emailed", get: (d) => (d.email_sent ? "YES" : "-") }],
      catering: [{ label: "Date", get: (l) => (l.check_date ? formatYmd(l.check_date) : formatDateTime(l.submitted_at)) }, { label: "Unit", get: (l) => l.attraction_name }, { label: "Check", get: (l) => cateringSessionLabel(l.catering_type) }, { label: "Result", get: (l) => (l.overall_result || "-").toUpperCase() }, { label: "Signed By", get: (l) => sb(l.signed_by_name, l.signed_by_initials) }],
      cleaning: [{ label: "Date", get: (l) => (l.check_date ? formatYmd(l.check_date) : formatDateTime(l.submitted_at)) }, { label: "Unit", get: (l) => l.attraction_name }, { label: "Result", get: (l) => (l.overall_result || "-").toUpperCase() }, { label: "Signed By", get: (l) => sb(l.signed_by_name, l.signed_by_initials) }],
    }[type];
    if (kind === "csv") { downloadCsv(`${type}.csv`, data.map((r) => Object.fromEntries(cols.map((c) => [c.label, c.get(r)])))); return; }
    setBusy(true);
    await new Promise((r) => setTimeout(r, 60));
    try {
      // Daily Checks + Wind Checks → full itemized, chronological multi-day check sheets
      if (type === "checks" || type === "wind") {
        exportDetailedLogs(t.label, company, data, type, { rangeLabel: rangeLabel() });
      } else if (type === "cleaning") {
        exportDetailedLogs(t.label, company, data, "cleaning", { rangeLabel: rangeLabel() });
      } else if (type === "catering") {
        // EHO Audit Pack: append allergen matrices + master cleaning schedules and
        // merge cleaning execution logs into the chronological check sheets.
        const units = attractions.filter((a) => a.category === "catering" && (aid === "all" || a.id === aid));
        const allergenMatrices = [], cleaningSchedules = [];
        for (const u of units) {
          try { const mx = await apiGet(`/attractions/${u.id}/allergen-matrix`); if (mx && (mx.dishes || []).length) allergenMatrices.push({ unitName: u.name, matrix: mx }); } catch (e) { /* ignore */ }
          try { const cs = await apiGet(`/attractions/${u.id}/cleaning-schedule`); if (cs && cs.length) cleaningSchedules.push({ unitName: u.name, items: cs }); } catch (e) { /* ignore */ }
        }
        let cleaningLogs = [];
        try {
          let cp = "/logs?log_type=cleaning";
          if (aid !== "all") cp += `&attraction_id=${aid}`;
          if (from) cp += `&date_from=${from}`;
          if (to) cp += `&date_to=${to}`;
          cleaningLogs = await apiGet(cp);
        } catch (e) { /* ignore */ }
        exportDetailedLogs(t.label, company, [...data, ...(cleaningLogs || [])], "catering", { rangeLabel: rangeLabel(), allergenMatrices, cleaningSchedules });
      } else if (type === "delivery") {
        exportDeliveries(company, data, { rangeLabel: rangeLabel() });
      } else {
        const photos = [];
        if (type === "incidents") data.forEach((i) => (i.photos || []).forEach((p, idx) => photos.push({ label: `${i.attraction_name} — ${i.injured_name} (${idx + 1})`, dataUri: p })));
        else if (type === "maintenance") data.forEach((m) => { if (m.photo) photos.push({ label: `${m.attraction_name} — ${workTypeLabel(m.work_type)}`, dataUri: m.photo }); });
        printReport(t.label, company, data, cols, photos);
      }
    } catch (e) { setMsg(e?.message || "Export failed"); } finally { setBusy(false); }
  };

  return (
    <div className="fade-up">
      {busy && <GeneratingOverlay />}
      <div style={{ display: "flex", alignItems: "center", gap: S.sm, padding: S.lg }}>
        <button className="pressable" onClick={() => nav(-1)}><Icon name="chevron-back" size={28} color={C.text} /></button>
        <div className="display" style={{ color: C.text, fontSize: 20, fontWeight: 900 }}>Reports Center</div>
      </div>
      <div style={{ padding: S.lg }}>
        <div style={{ color: C.sub, fontSize: 13, fontWeight: 700, marginBottom: S.sm, textTransform: "uppercase" }}>Report category</div>
        <div data-testid="report-category-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: S.sm }}>
          {TYPES.map((t) => {
            const active = type === t.key;
            return (
              <button key={t.key} data-testid={`report-${t.key}`} onClick={() => setType(t.key)} style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: S.sm, background: C.card, borderRadius: R.md, padding: S.lg, border: `2px solid ${active ? t.color : C.border}`, textAlign: "left", aspectRatio: "1 / 0.72", position: "relative", cursor: "pointer" }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: active ? t.color : C.card2, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon name={t.icon} size={24} color={active ? C.black : t.color} />
                </div>
                <span style={{ color: C.text, fontWeight: 800, fontSize: 15 }}>{t.label}</span>
                {active && <div style={{ position: "absolute", top: 10, right: 10 }}><Icon name="checkmark-circle" size={20} color={t.color} /></div>}
              </button>
            );
          })}
        </div>
        <div style={{ color: C.sub, fontSize: 13, fontWeight: 700, margin: `${S.md}px 0 ${S.sm}px`, textTransform: "uppercase" }}>Attraction</div>
        <select data-testid="report-attraction" value={aid} onChange={(e) => setAid(e.target.value)} style={{ width: "100%", background: C.card2, borderRadius: R.md, border: `1px solid ${C.borderStrong}`, color: C.text, fontSize: 16, minHeight: 50, padding: `0 ${S.md}px`, marginBottom: S.md }}>
          <option value="all">All attractions</option>
          {attractions.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <div style={{ color: C.sub, fontSize: 13, fontWeight: 700, margin: `${S.md}px 0 ${S.sm}px`, textTransform: "uppercase" }}>Date range</div>
        <DateRangePicker from={from || null} to={to || null} onChange={(f, t) => { setFrom(f || ""); setTo(t || ""); }} />
        <div style={{ height: S.sm }} />
        {!!msg && <div style={{ color: C.red, fontWeight: 700, marginBottom: S.sm }}>{msg}</div>}
        <div style={{ display: "flex", gap: S.sm }}>
          <Button title="Download PDF" testID="report-pdf" onPress={() => run("pdf")} />
          <Button title="CSV" testID="report-csv" variant="dark" onPress={() => run("csv")} />
        </div>
        <div style={{ height: S.xxl }} />
      </div>
    </div>
  );
}
